import { Controller } from "@hotwired/stimulus"
import { chatContextPath } from "src/routes"

export default class ChatController extends Controller {
  static values = { issueType: String, orderId: String, secureToken: String, itemNames: String }

  declare readonly issueTypeValue: string
  declare readonly orderIdValue: string
  declare readonly secureTokenValue: string
  declare readonly itemNamesValue: string

  connect() {
    const header = document.querySelector<HTMLElement>('header.is-sticky')
    if (header) {
      document.documentElement.style.setProperty('--nav-height', `${header.getBoundingClientRect().height}px`)
    }
    void this.initChat()
  }

  private async initChat() {
    await this.identifyVisitor()

    // clear({ resetWidget: true }) re-runs hsConversationsOnReady, so guard
    // against init calling itself again
    let started = false

    const init = () => {
      const hs = window.HubSpotConversations
      if (!hs || started) return
      started = true

      if (hs.widget.status().loaded) hs.clear({ resetWidget: true })

      const onReady = () => {
        hs.off("conversationsReady", onReady)
        hs.widget.open()
      }

      hs.on("conversationsReady", onReady)
      hs.widget.load({ widgetOpen: false })
    }

    if (window.HubSpotConversations) {
      init()
    } else {
      window.hsConversationsOnReady = [
        ...(window.hsConversationsOnReady ?? []),
        init
      ]
    }
  }

  private async identifyVisitor(): Promise<void> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 8_000)

    try {
      const response = await fetch(chatContextPath(), {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({
          issue_type: this.issueTypeValue,
          order_id: this.orderIdValue,
          item_names: this.itemNamesValue,
        }),
      })

      if (!response.ok) return

      const { email, token } = await response.json() as { email: string; token: string }

      window.hsConversationsSettings = {
        ...window.hsConversationsSettings,
        identificationEmail: email,
        identificationToken: token,
      }
    } catch (err) {
      if (err instanceof Error && err.name !== "AbortError") {
        console.warn("[HubSpot] Visitor identification failed:", err)
      }
    } finally {
      clearTimeout(timeout)
    }
  }

  private csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
  }
}